import React, { useContext, useEffect, useState } from "react";
import { useRequest } from "../hooks";
import DataContext from "./DataContext";
import {
  ActivityProps,
  ClientProps,
  ContactProps,
  CustomerProps,
  FilterProps,
  InvoiceProps,
  ListComponentProps,
  ProjectProps,
  ProjectTemplateProps,
  QuickbooksCompanyProps,
  StatusProps,
  TagProps,
  TaskProps,
  TemplateProps,
  ToDoProps,
  UserAccountProps,
  UserProps,
  GroupProps,
  FileProps,
  PaginationProps,
} from "../interfaces/interfaces";
import {
  ALERT_INITIAL_STATE,
  ERROR_ENCRYPTED,
  PAGINATION_INITIAL_STATE,
  PROJECT_INITIAL_STATE,
  STATUS_INITIAL_LIST,
} from "../utils/data";
import AuthContext from "./AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "./NotificationProvider";
import { Dayjs } from "dayjs";
interface Props {
  children: JSX.Element | JSX.Element[];
}

const LIST_INITIAL_STATE = [{ label: "", value: "" }];

const DataProvider = ({ children }: Props) => {
  const { pathname } = useLocation();
  const { handleRequest, handleRequestFile } = useRequest();
  const {
    onLogout,
    changeProfileInfo,
    clientIdSelected,
    role,
    handleGoogleTaskLogin,
  } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState({ status: false, message: "" });
  const [isSuccess, setIsSuccess] = useState({ status: false, message: "" });
  const [statusList, setStatusList] =
    useState<StatusProps[]>(STATUS_INITIAL_LIST);
  const [isModalStatusOpen, SetIsModalStatusOpen] = useState(false);
  const [statusToEdit, setStatusToEdit] = useState<StatusProps | null>(null);
  const [invoiceList, setInvoiceList] = useState<InvoiceProps[]>([]);
  const [customerList, setCustomerList] = useState<CustomerProps[]>([]);
  const [customerByClient, setCustomerByClient] = useState<CustomerProps[]>([]);
  const [customerByClientPaginated, setCustomerByClientPaginated] = useState<{
    data: CustomerProps[];
    pagination: PaginationProps;
  }>({
    data: [],
    pagination: PAGINATION_INITIAL_STATE,
  });
  const [projectInformation, setProjectInformation] = useState<ProjectProps>(
    PROJECT_INITIAL_STATE,
  );
  const [isWarning, setIsWarning] = useState<{
    status: boolean;
    message: string;
    actionLabel?: string;
    action?: () => void;
  }>({ status: false, message: "" });
  const [customerOptions, setCustomerOptions] = useState<ListComponentProps[]>(
    [],
  );
  const [client, setClient] = useState<null | ClientProps>(null);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [userAccount, setUserAccount] = useState<UserAccountProps[]>([]);
  const [termOptions, setTermOptions] =
    useState<ListComponentProps[]>(LIST_INITIAL_STATE);
  const [templateList, setTemplateList] = useState<TemplateProps[]>([]);
  const [usersList, setUserList] = useState<UserProps[]>([]);
  const [userOptions, setUserOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [projectListPaginated, setProjectListPaginated] = useState<{
    data: ProjectProps[];
    pagination: PaginationProps;
  }>({
    data: [],
    pagination: PAGINATION_INITIAL_STATE,
  });
  const [projectList, setProjectList] = useState<ProjectProps[]>([]);
  const [projectTemplateList, setProjectTemplateList] = useState<
    ProjectTemplateProps[]
  >([]);
  const [activityList, setActivityList] = useState<{
    data: ActivityProps[];
    pagination: PaginationProps;
  }>({
    data: [],
    pagination: PAGINATION_INITIAL_STATE,
  });
  const [taskList, setTaskList] = useState<{
    data: TaskProps[];
    pagination: PaginationProps;
  }>({
    data: [],
    pagination: PAGINATION_INITIAL_STATE,
  });
  const [taskDashboard, setTaskDashboard] = useState<{
    data: TaskProps[];
    pagination: PaginationProps;
  }>({
    data: [],
    pagination: PAGINATION_INITIAL_STATE,
  });
  const [contactList, setContactList] = useState<ContactProps[]>([]);
  const [contactListPaginated, setContactListPaginated] = useState<{
    data: ContactProps[];
    pagination: PaginationProps;
  }>({
    data: [],
    pagination: PAGINATION_INITIAL_STATE,
  });
  const [todoList, setToDoList] = useState<ToDoProps[]>([]);
  const [tagList, setTagList] = useState<TagProps[]>([]);
  const { showNotification } = useNotification();
  const [projectModal, setProjectModal] = useState<{
    open: boolean;
    create: boolean;
    update: boolean;
    selected: string[];
    payload: ProjectProps | null;
  } | null>(null);
  const [activityModal, setActivityModal] = useState<{
    open: boolean;
    create: boolean;
    update: boolean;
    payload: ActivityProps | null;
  } | null>(null);
  const [taskModal, setTaskModal] = useState<{
    open: boolean;
    create: boolean;
    update: boolean;
    payload: TaskProps | null;
  } | null>(null);
  const [contactModal, setContactModal] = useState<{
    open: boolean;
    create: boolean;
    update: boolean;
    payload: ContactProps | null;
  } | null>(null);
  const [projectSelected, setProjectSelected] = useState<string | null>(null);
  const [templateSelected, setTemplateSelected] =
    useState<ProjectTemplateProps | null>(null);
  const [projectsByTemplate, setProjectsByTemplate] = useState<ProjectProps[]>(
    [],
  );
  const [userGroups, setUserGroups] = useState<GroupProps[]>([]);
  const [statusByTemplate, setStatusByTemplate] = useState<
    { name: string; _id: string }[]
  >([]);
  const [customerFiles, setCustomerFiles] = useState<{
    data: FileProps[];
    pagination: PaginationProps;
  }>({
    data: [],
    pagination: PAGINATION_INITIAL_STATE,
  });
  const [files, setFiles] = useState<{
    data: FileProps[];
    pagination: PaginationProps;
  }>({
    data: [],
    pagination: PAGINATION_INITIAL_STATE,
  });
  const [downloadedFile, setDownloadedFile] = useState("");

  useEffect(() => {
    setTimeout(() => {
      setIsSuccess(ALERT_INITIAL_STATE);
      setIsError(ALERT_INITIAL_STATE);
    }, 3000);
  }, [isError, isSuccess]);

  useEffect(() => {
    let userOptions = usersList
      .filter((user) => user.role.permissions !== "CLIENT")
      .map((user) => ({
        label: user.name,
        value: user._id,
      }));
    setUserOptions(userOptions);
  }, [usersList]);

  useEffect(() => {
    setIsError(ALERT_INITIAL_STATE);
    setIsSuccess(ALERT_INITIAL_STATE);
    setContactModal(null);
    setActivityModal(null);
    setProjectModal(null);
    setTaskModal(null);
    handleChangeProjectSelected(null);
  }, []);

  useEffect(() => {
    if (role && role !== "CLIENT")
      clientIdSelected && getData("tags", clientIdSelected);
    handleChangeProjectSelected(null);
  }, [clientIdSelected, role]);

  useEffect(() => {
    let optionsCustomer: ListComponentProps[] = [];
    customerList.forEach((customer) => {
      optionsCustomer.push({
        label:
          customer.DisplayName && customer.FullyQualifiedName
            ? customer.DisplayName + " (" + customer.FullyQualifiedName + ")"
            : customer.CompanyName,
        value: customer._id,
      });
    });
    optionsCustomer.length > 0 && setCustomerOptions(optionsCustomer);
  }, [customerList]);

  useEffect(() => {
    if (projectTemplateList.length > 0 && !projectSelected) {
      let selected = projectTemplateList[0]._id;
      selected &&
        getDataById({
          type: "project-template",
          endpoint: `project-template/${clientIdSelected}/${selected}`,
        });
    }
  }, [projectTemplateList]);

  useEffect(() => {
    if (projectSelected) {
      getCustomersListByProject({
        idClient: clientIdSelected || "",
        idProject: projectSelected || "",
        filters: { customer: null },
      });
    }
  }, [projectSelected]);

  const handleChangeProjectSelected = (value: string | null) => {
    setProjectSelected(value);
  };

  const openItemModal = (
    type: "task" | "project" | "activity" | "contact",
    create: boolean,
    payload?: any,
    itemId?: string,
    selected?: string[],
  ) => {
    if (itemId && clientIdSelected) {
      const endpoint = `${type}/${clientIdSelected}/${itemId}`;
      handleRequest({
        endpoint,
        onSuccess: (response) => {
          response.data
            ? openModal(type, false, response.data)
            : showNotification("warning", "Warning", "Please try again");
        },
        onError: (response) => {
          showNotification(
            "warning",
            "Warning",
            response.error?.message || "Please try again",
          );
        },
      });
    } else {
      openModal(type, create, payload, selected);
    }
  };

  const openModal = (
    type: "task" | "activity" | "project" | "contact",
    create: boolean,
    payload?: any,
    selected?: string[],
  ) => {
    switch (type) {
      case "task":
        setTaskModal({
          open: true,
          create: create,
          update: !create,
          payload: (payload as TaskProps) || null,
        });
        break;
      case "activity":
        setActivityModal({
          open: true,
          create: create,
          update: !create,
          payload: (payload as ActivityProps) || null,
        });
        break;
      case "project":
        setProjectModal({
          open: true,
          create: create,
          update: !create,
          selected: selected || [],
          payload: (payload as ProjectProps) || null,
        });
        break;
      case "contact":
        setContactModal({
          open: true,
          create: create,
          update: !create,
          payload: (payload as ContactProps) || null,
        });
        break;
      default:
        break;
    }
  };

  const closeItemModal = (
    type: "task" | "project" | "activity" | "contact",
  ) => {
    switch (type) {
      case "task":
        setTaskModal(null);
        break;
      case "activity":
        setActivityModal(null);
        break;
      case "project":
        setProjectModal(null);
        break;
      case "contact":
        setContactModal(null);
        break;
      default:
        break;
    }
  };

  const handleChangeProjectModalStatus = (
    newState: {
      open: boolean;
      create: boolean;
      update: boolean;
      selected: string[];
      payload: ProjectProps | null;
    } | null,
  ) => {
    setProjectModal(newState);
  };

  const handleTour = (value: boolean) => {
    setIsTourOpen(value);
  };

  const handleWarning = (
    status: boolean,
    message: string,
    actionLabel?: string,
    action?: () => void,
  ) => {
    setIsWarning({ status, message, actionLabel, action });
  };

  const handleSuccess = (status: boolean, message: string) => {
    setIsSuccess({ status, message });
  };

  const formatTermList = (list: { Name: string; Id: string }[]) => {
    let options: ListComponentProps[] = [];
    list.forEach((status) => {
      options.push({
        label: status.Name,
        value: status.Id,
      });
    });
    options.length > 0 && setTermOptions(options);
  };

  const resetContacts = () => {
    setContactList([]);
  };
  const getData = (
    endpoint:
      | "status"
      | "customer"
      | "invoice"
      | "deposit"
      | "credit"
      | "payment"
      | "journal"
      | "client"
      | "user-account"
      | "profile-info"
      | "term"
      | "templates"
      | "users"
      | "projects"
      | "projects-paginated"
      | "projects"
      | "project-templates"
      | "activities-paginated"
      | "tasks"
      | "task-dashboard"
      | "contacts"
      | "contacts-paginated"
      | "todos"
      | "tags"
      | "groups"
      | "files",
    idClient: string,
    search?: string,
    filters?: any,
    showAlert?: boolean,
  ) => {
    setIsError(ALERT_INITIAL_STATE);
    setIsLoading(true);
    let options: RequestInit = {
      method: "GET",
    };
    const { page, perPage, ...restFilters } = filters || {};
    let option = endpoint;
    if (endpoint === "task-dashboard" || endpoint === "tasks") {
      endpoint = "tasks-paginated" as typeof endpoint;
    }
    let searchEncoded = search && `search=${encodeURIComponent(search)}`;
    let filtersEncoded =
      restFilters && encodeURIComponent(JSON.stringify(restFilters));
    if (filtersEncoded) {
      searchEncoded
        ? (searchEncoded += `&filters=${filtersEncoded}`)
        : (searchEncoded = `filters=${filtersEncoded}`);
    }
    if (page || perPage) {
      searchEncoded
        ? (searchEncoded += `&page=${page}&perPage=${perPage}`)
        : (searchEncoded = `page=${page}&perPage=${perPage}`);
    }
    handleRequest({
      endpoint: searchEncoded
        ? `${endpoint}/${idClient}?${searchEncoded}`
        : `${endpoint}/${idClient}`,
      options,
      onSuccess: (response) => {
        switch (option) {
          case "status":
            setStatusList(response.data);
            break;
          case "customer":
            setCustomerList(response.data);
            break;
          case "client":
            setClient(response.data);
            break;
          case "term":
            formatTermList(response.data);
            break;
          case "profile-info":
            changeProfileInfo(response.data);
            break;
          case "templates":
            setTemplateList(response.data);
            break;
          case "users":
            setUserList(response.data);
            break;
          case "projects":
            setProjectList(response.data);
            break;
          case "projects-paginated":
            setProjectListPaginated(response.data);
            break;
          case "project-templates":
            setProjectTemplateList(response.data);
            break;
          case "activities-paginated":
            setActivityList(response.data);
            break;
          case "tasks":
            setTaskList(response.data);
            break;
          case "task-dashboard":
            setTaskDashboard(response.data);
            break;
          case "contacts":
            setContactList(response.data);
            break;
          case "contacts-paginated":
            setContactListPaginated(response.data);
            break;
          case "todos":
            setToDoList(response.data);
            break;
          case "tags":
            setTagList(response.data);
            break;
          case "groups":
            setUserGroups(response.data);
            break;
          case "files":
            setFiles(response.data);
            break;
          default:
            setInvoiceList(response.data);
            break;
        }
        setIsLoading(false);
      },
      onError: (e) => {
        setIsLoading(false);
        setIsError({ status: true, message: "Please try again" });
      },
    });
  };
  const getDataById = ({
    type,
    endpoint,
    search,
    filters,
    showAlert,
  }: {
    type: "project-template" | "template-status" | "customer-files";
    endpoint: string;
    search?: string;
    filters?: any;
    showAlert?: boolean;
  }) => {
    setIsError(ALERT_INITIAL_STATE);
    showAlert && setIsLoading(true);
    let options: RequestInit = {
      method: "GET",
    };
    const { page, perPage, ...restFilters } = filters || {};
    let searchEncoded = search && `search=${encodeURIComponent(search)}`;
    let filtersEncoded =
      filters && encodeURIComponent(JSON.stringify(restFilters));
    if (filtersEncoded) {
      searchEncoded
        ? (searchEncoded += `&filters=${filtersEncoded}`)
        : (searchEncoded = `filters=${filtersEncoded}`);
    }
    if (page || perPage) {
      searchEncoded
        ? (searchEncoded += `&page=${page}&perPage=${perPage}`)
        : (searchEncoded = `page=${page}&perPage=${perPage}`);
    }
    handleRequest({
      endpoint: searchEncoded ? `${endpoint}?${searchEncoded}` : `${endpoint}`,
      options,
      onSuccess: (response) => {
        switch (type) {
          case "project-template":
            if (response.data.projects) {
              setTemplateSelected(response.data);
              setProjectsByTemplate(response.data.projects);
              setProjectInformation(
                response.data?.projects[0] || PROJECT_INITIAL_STATE,
              );
              setProjectSelected(response.data?.projects[0]?._id || null);
            } else {
              setProjectListPaginated({
                data: [],
                pagination: PAGINATION_INITIAL_STATE,
              });
              projectListPaginated.data.length > 0
                ? setProjectSelected(projectListPaginated.data[0]._id)
                : setProjectSelected(null);
              projectListPaginated.data.length > 0
                ? setProjectInformation(projectListPaginated.data[0])
                : setProjectInformation(PROJECT_INITIAL_STATE);
            }
            break;
          case "template-status":
            if (response.data) {
              setStatusByTemplate(response.data?.projectStatus);
            }
            break;
          case "customer-files":
            if (response.data) {
              setCustomerFiles(response.data);
            }
            break;
          default:
            break;
        }
        setIsLoading(false);
      },
      onError: (e) => {
        setIsLoading(false);
      },
    });
  };

  const getCustomersListByClient = ({
    idClient,
    filters,
    search,
    next,
  }: {
    idClient: string;
    filters?: { customer: string[] | null; location?: string };
    search?: string | null;
    next?: () => void;
  }) => {
    setIsError(ALERT_INITIAL_STATE);
    let options: RequestInit = {
      method: "GET",
    };
    let searchEncoded = search && encodeURIComponent(search);
    let apiEndpoint = searchEncoded
      ? `customer/${idClient}?search=${searchEncoded}`
      : `customer/${idClient}`;
    if (filters) {
      if (searchEncoded) {
        apiEndpoint = `${apiEndpoint}&filters=${JSON.stringify(filters)}`;
      } else {
        apiEndpoint = `${apiEndpoint}?filters=${JSON.stringify(filters)}`;
      }
    }
    handleRequest({
      endpoint: apiEndpoint,
      options,
      onSuccess: (response) => {
        setCustomerByClient(response.data);
        next?.();
        setIsLoading(false);
      },
      onError: (e) => {
        setIsLoading(false);
        setIsError({ status: true, message: "Please try again" });
        // if (e.message === "Not Authorized") onLogout(() => navigate("/login"));
      },
    });
  };
  const getCustomersListByClientPaginated = ({
    idClient,
    filters,
    search,
    next,
  }: {
    idClient: string;
    filters?: any;
    search?: string | null;
    next?: () => void;
  }) => {
    setIsError(ALERT_INITIAL_STATE);
    let options: RequestInit = {
      method: "GET",
    };
    const { page, perPage, ...restFilters } = filters || {};
    let searchEncoded = search && `search=${encodeURIComponent(search)}`;
    let filtersEncoded =
      restFilters && encodeURIComponent(JSON.stringify(restFilters));
    if (filtersEncoded) {
      searchEncoded
        ? (searchEncoded += `&filters=${filtersEncoded}`)
        : (searchEncoded = `filters=${filtersEncoded}`);
    }
    if (page || perPage) {
      searchEncoded
        ? (searchEncoded += `&page=${page}&perPage=${perPage}`)
        : (searchEncoded = `page=${page}&perPage=${perPage}`);
    }
    handleRequest({
      endpoint: searchEncoded
        ? `customer-paginated/${idClient}?${searchEncoded}`
        : `customer-paginated/${idClient}`,
      options,
      onSuccess: (response) => {
        setCustomerByClientPaginated(response.data);
        next?.();
        setIsLoading(false);
      },
      onError: (e) => {
        setIsLoading(false);
        setIsError({ status: true, message: "Please try again" });
      },
    });
  };
  const getCustomersListByProject = ({
    idClient,
    idProject,
    filters,
    search,
    next,
  }: {
    idClient: string;
    idProject: string;
    filters?: { customer: string[] | null };
    search?: string | null;
    next?: () => void;
  }) => {
    setIsError(ALERT_INITIAL_STATE);
    let options: RequestInit = {
      method: "GET",
    };
    let searchEncoded = search && encodeURIComponent(search);
    let apiEndpoint = searchEncoded
      ? `project/${idClient}/${idProject}?search=${searchEncoded}`
      : `project/${idClient}/${idProject}`;
    if (filters) {
      if (searchEncoded) {
        apiEndpoint = `${apiEndpoint}&filters=${JSON.stringify(filters)}`;
      } else {
        apiEndpoint = `${apiEndpoint}?filters=${JSON.stringify(filters)}`;
      }
    }
    handleRequest({
      endpoint: apiEndpoint,
      options,
      onSuccess: (response) => {
        setProjectInformation(response.data);
        next?.();
        setIsLoading(false);
      },
      onError: (e) => {
        setIsLoading(false);
        setIsError({ status: true, message: "Please try again" });
      },
    });
  };

  const fetchUserAccounts = (idUser: string) => {
    try {
      setIsLoading(true);
      setIsError(ALERT_INITIAL_STATE);
      handleRequest({
        endpoint: `user-account/${idUser}`,
        onSuccess: (response) => {
          if (response.data) {
            setUserAccount(response.data);
          } else {
            setIsLoading(false);
            setIsError({ status: true, message: "Not Authorized" });
          }
        },
        onError: (e) => {
          setIsLoading(false);
          setIsError({ status: true, message: "Not Authorized" });
          if (e.message === "Not Authorized")
            onLogout(() => navigate(`/login?message=${ERROR_ENCRYPTED}`));
        },
      });
    } catch (e) {
      setIsLoading(false);
      setIsError({ status: true, message: "Not Authorized" });
    }
  };

  const getQbCompanyInfo = (
    realmId: string,
    next: (data: QuickbooksCompanyProps) => void,
  ) => {
    try {
      setIsLoading(true);
      return handleRequest({
        endpoint: `qbcompany/${realmId}`,
        onSuccess: (response) => {
          if (response.data) {
            next(response.data);
          } else {
            return null;
          }
        },
        onError: (e) => {
          return null;
        },
      });
    } catch (e) {
      return null;
    }
  };

  const getReportData = (
    endpoint: "report" | "logs",
    idClient: string,
    filters: FilterProps,
    next?: (response: any) => void,
  ) => {
    setIsLoading(true);
    setIsError(ALERT_INITIAL_STATE);
    let options: RequestInit = {
      method: "GET",
    };
    handleRequest({
      endpoint: `${endpoint}/${idClient}/?filters=${JSON.stringify(filters)}`,
      options,
      onSuccess: (response) => {
        if (response.data.length > 0) {
          setIsSuccess({ status: true, message: "Data fetched successfully" });
          next && next(response.data);
        } else {
          setIsError({ status: true, message: "No data" });
        }
        setIsLoading(false);
      },
      onError: (e) => {
        setIsLoading(false);
        setIsError({ status: true, message: "Please try again" });
        // if (e.message === "Not Authorized") onLogout(() => navigate("/login"));
      },
    });
  };

  const handleCreateData = ({
    endpoint,
    idClient,
    data,
    next,
    nextOnFailed,
    showAlert = true,
  }: {
    endpoint: string;
    idClient: string;
    data: any;
    next?: (newData: any) => void;
    nextOnFailed?: () => void;
    showAlert?: boolean;
  }) => {
    showAlert && setIsLoading(true);
    setIsError(ALERT_INITIAL_STATE);
    let options: RequestInit = {
      method: "POST",
      body: JSON.stringify(data),
    };
    handleRequest({
      endpoint: endpoint + "/" + idClient,
      options,
      onSuccess: (response) => {
        next && next(response.data);
        setIsLoading(false);
        if (response.data) {
          showAlert &&
            showNotification(
              "success",
              "Created",
              response.data?.message || "Your data has been saved.",
            );
        } else {
          setIsSuccess({ status: true, message: "Created" });
        }
        setIsLoading(false);
      },
      onError: (e) => {
        setIsLoading(false);
        showAlert &&
          showNotification(
            "warning",
            "Warning",
            e?.message || e?.error?.message || "Something has ocurred",
          );
        nextOnFailed?.();
      },
    });
  };

  const handleEditData = ({
    endpoint,
    newData,
    next,
    nextOnFailed,
    showAlert = true,
  }: {
    endpoint: string;
    newData: any;
    next?: () => void;
    nextOnFailed?: (e: any) => void;
    showAlert?: boolean;
  }) => {
    showAlert && setIsLoading(true);
    setIsError(ALERT_INITIAL_STATE);
    let options: RequestInit = {
      method: "PUT",
      body: JSON.stringify({ ...newData }),
    };
    handleRequest({
      endpoint,
      options,
      onSuccess: (response) => {
        setIsLoading(false);
        showAlert &&
          showNotification(
            "success",
            "Updated",
            response.data?.message || "Your data has been saved.",
          );
        next?.();
      },
      onError: (e) => {
        setIsLoading(false);
        showAlert &&
          showNotification(
            "warning",
            "Warning",
            e.error?.message || e.message || "Something has ocurred",
          );
        nextOnFailed && nextOnFailed(e);
        // // if (e.message === "Not Authorized") onLogout(() => navigate("/login"));
      },
    });
  };

  const handleDeleteData = ({
    endpoint,
    next,
  }: {
    endpoint: string;
    next: () => void;
  }) => {
    setIsLoading(true);
    let options: RequestInit = {
      method: "DELETE",
    };
    handleRequest({
      endpoint,
      options,
      onSuccess: () => {
        setIsLoading(false);
        setIsSuccess({ status: true, message: "Item Deleted" });
        next();
      },
      onError: (e) => {
        setIsLoading(false);
        setIsError({ status: true, message: e.message || "Please try again" });
        // if (e.message === "Not Authorized") onLogout(() => navigate("/login"));
      },
    });
  };

  const handleBulkEdit = ({
    endpoint,
    items,
    newData,
    next,
  }: {
    endpoint: string;
    items: string[];
    newData: any;
    next?: () => void;
  }) => {
    setIsLoading(true);
    let options: RequestInit = {
      method: "PUT",
      body: JSON.stringify({ idList: items, ...newData }),
    };
    handleRequest({
      endpoint,
      options,
      onSuccess: () => {
        next?.();
        setIsSuccess({ status: true, message: "Updated" });
        setIsLoading(false);
      },
      onError: (e) => {
        setIsLoading(false);
        setIsError({
          status: true,
          message: e.message || "Please try again",
        });
        // if (e.message === "Not Authorized") onLogout(() => navigate("/login"));
      },
    });
  };

  const handleBulkDeleteData = (
    endpoint:
      | "status"
      | "invoice"
      | "payment"
      | "deposit"
      | "credit"
      | "journal",
    idClient: string,
    idList: string[],
  ) => {
    setIsLoading(true);
    setIsError(ALERT_INITIAL_STATE);
    let options: RequestInit = {
      method: "DELETE",
      body: JSON.stringify({ idList }),
    };
    handleRequest({
      endpoint: `${endpoint}-bulk/${idClient}`,
      options,
      onSuccess: () => {
        setIsLoading(false);
        setIsSuccess({ status: true, message: "Items Deleted" });
        getData(endpoint, idClient);
      },
      onError: (e) => {
        setIsLoading(false);
        setIsError({
          status: true,
          message: e.message || "Please try again",
        });
        // if (e.message === "Not Authorized") onLogout(() => navigate("/login"));
      },
    });
  };

  const createOrUpdateTask = async (
    customerInfo: CustomerProps,
    values: TaskProps,
    idClient: string,
    idTask?: string,
    next?: () => void,
  ) => {
    let filters: {
      active: boolean;
      project?: string;
      customer?: string;
      assignedTo?: boolean;
      $or?: any;
    } = {
      active: true,
    };
    if (values.origin === "dashboard") {
      filters.assignedTo = true;
    } else if (values.origin === "project") {
      projectSelected && (filters.project = projectSelected);
      values.project &&
        (filters.project = values.project._id || (values.project as any));
    } else {
      customerInfo._id && (filters.customer = customerInfo._id);
      projectSelected && (filters.project = projectSelected);
    }
    if (taskModal && taskModal.create) {
      const idCustomer = values.customer
        ? values.customer
        : values.customers
          ? values.customers
          : customerInfo._id;
      handleCreateData({
        endpoint: "tasks",
        idClient,
        data: {
          ...values,
          customer: idCustomer,
          project: values.project ? values.project : projectSelected,
        },
        showAlert: false,
        next: (response) => {
          if (values.createInGoogleTasks) {
            handleCreateTaskGoogle({
              idClient,
              values: {
                description: values.description,
                notes: values.notes,
                startDate: values.startDate || null,
              },
            });
          }
          if (values.createInGoogleCalendar) {
            handleCreateEventCalendarGoogle({
              idClient,
              idTask: response._id,
            });
          }
          if (next) {
            next();
          }
          if (!values.customers) {
            getData("tasks", idClient, undefined, filters);
          }
          if (values.origin === "dashboard") {
            getData("tasks", idClient, undefined, {
              assignedTo: true,
              active: true,
              page: 1,
              perPage: 5,
            });
          } else if (values.origin === "project") {
            getData("tasks", idClient, undefined, {
              project: values.project,
              active: true,
              page: 1,
              perPage: 5,
            });
          } else {
            getData("tasks", idClient, undefined, {
              ...filters,
              page: 1,
              perPage: 5,
            });
          }
          closeItemModal("task");
        },
      });
    } else if (taskModal && taskModal.update && idTask) {
      handleEditData({
        endpoint: `task/${idClient}/${idTask}`,
        newData: values,
        next: () => {
          if (values.origin === "dashboard") {
            getData("tasks", idClient, undefined, {
              assignedTo: true,
              active: true,
              page: 1,
              perPage: 5,
            });
          } else if (values.origin === "project") {
            getData("tasks", idClient, undefined, {
              project: values.project,
              active: true,
              page: 1,
              perPage: 5,
            });
          } else {
            getData("tasks", idClient, undefined, {
              ...filters,
              page: 1,
              perPage: 5,
            });
          }
          closeItemModal("task");
        },
      });
      handleEditEventCalendarGoogle({
        idClient,
        values: {
          ...values,
          _id: idTask,
        },
      });
    }
  };

  const handleCreateTaskGoogle = ({
    idClient,
    values,
  }: {
    idClient: string;
    values: { description: string; notes: string; startDate: Dayjs | null };
  }) => {
    handleCreateData({
      endpoint: "google/tasks",
      idClient,
      data: {
        title: values.description,
        notes: values.notes,
        due: values.startDate,
      },
      nextOnFailed: () => handleGoogleTaskLogin(),
    });
  };

  const handleCreateEventCalendarGoogle = ({
    idClient,
    idTask,
  }: {
    idClient: string;
    idTask: string;
  }) => {
    let options: RequestInit = {
      method: "POST",
      body: JSON.stringify({}),
    };
    handleRequest({
      endpoint: `google/calendar/${idClient}/${idTask}`,
      options,
      onSuccess: (response) => {
        showNotification(
          "success",
          "Created",
          response.data?.message || "Your data has been saved.",
        );
      },
      onError: (e) => {
        setIsLoading(false);
        showNotification(
          "warning",
          "Warning",
          e?.message || e?.error?.message || "Something has ocurred",
        );
      },
    });
  };
  const handleEditEventCalendarGoogle = ({
    idClient,
    values,
  }: {
    idClient: string;
    values: TaskProps;
  }) => {
    handleEditData({
      endpoint: `google/calendar/${idClient}/${values._id}`,
      newData: values,
    });
  };

  const createOrUpdateActivity = (
    customerInfo: CustomerProps,
    values: ActivityProps,
    idClient: string,
    idActivity?: string,
  ) => {
    let filters: {
      active: boolean;
      project?: string;
      customer?: string;
      assignedTo?: any;
      $or?: any;
      page: number;
      perPage: number;
    } = {
      active: true,
      page: 1,
      perPage: 5,
    };
    if (values.origin === "dashboard") {
      filters.assignedTo = true;
    } else if (values.origin === "project") {
      projectSelected && (filters.project = projectSelected);
      values.project &&
        (filters.project = values.project._id || (values.project as any));
    } else {
      customerInfo._id && (filters.customer = customerInfo._id);
      projectSelected && (filters.project = projectSelected);
      values.project &&
        (filters.project = values.project._id || (values.project as any));
    }
    if (activityModal && activityModal.create) {
      handleCreateData({
        endpoint: "activities",
        idClient,
        data: {
          ...values,
          customer: values.customers
            ? null
            : customerInfo._id || values.customer?._id,
          project: values.project ? values.project : projectSelected,
        },
        next: () => {
          getData("activities-paginated", idClient, undefined, filters);
          closeItemModal("activity");
        },
      });
    } else if (activityModal && activityModal.update && idActivity) {
      handleEditData({
        endpoint: `activity/${idClient}/${idActivity}`,
        newData: values,
        next: () => {
          getData("activities-paginated", idClient, undefined, filters);
          closeItemModal("activity");
        },
      });
    }
  };

  const createOrUpdateContact = (
    customerInfo: CustomerProps,
    values: ContactProps,
    idClient: string,
  ) => {
    let filters = {};
    if (customerInfo._id) {
      customerInfo._id && (filters = { customer: customerInfo._id });
    }
    if (contactModal && contactModal.create) {
      handleCreateData({
        endpoint: "contacts",
        idClient,
        data: {
          ...values,
        },
        next: () => {
          getData("contacts", idClient, undefined, filters);
          closeItemModal("contact");
        },
      });
    } else {
      handleEditData({
        endpoint: `contact/${idClient}/${values._id}`,
        newData: values,
        next: () => {
          getData("contacts", idClient, undefined, filters);
          closeItemModal("contact");
        },
      });
    }
  };

  const uploadFile = ({
    endpoint,
    formData,
    next,
    onError,
  }: {
    endpoint: string;
    formData: FormData;
    next?: (response: any) => void;
    onError?: () => void;
  }) => {
    setIsLoading(true);
    setIsError(ALERT_INITIAL_STATE);
    let options: RequestInit = {
      method: "POST",
      body: formData,
    };
    handleRequest({
      endpoint,
      options,
      onSuccess: (response) => {
        setIsLoading(false);
        next?.(response);
      },
      onError: (e) => {
        setIsLoading(false);
        onError?.();
        showNotification(
          "warning",
          "Warning",
          e?.message || e?.error?.message || "Something has ocurred",
        );
      },
    });
  };

  const downloadFile = ({
    endpoint,
    next,
  }: {
    endpoint: string;
    next: () => void;
  }) => {
    setIsError(ALERT_INITIAL_STATE);
    setIsLoading(true);
    let options: RequestInit = {
      method: "GET",
    };
    handleRequestFile({
      endpoint,
      options,
      next: async () => {
        setIsLoading(false);
      },
      nextOnFail: () => {
        setIsLoading(false);
        setDownloadedFile("");
        setIsError({ status: true, message: "Please try again" });
      },
    });
  };

  const createFile = (
    data: { type: string; data: Buffer },
    name: string,
    download: boolean,
  ) => {
    try {
      const url = window.URL.createObjectURL(
        new Blob([new Uint8Array(data.data).buffer], {
          type: data.type,
        }),
      );
      if (download) {
        var link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name);
        document.body.appendChild(link);
        link.click();
      } else {
        window.open(url);
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  return (
    <DataContext.Provider
      value={{
        dataLoading: isLoading,
        dataSuccess: isSuccess,
        dataError: isError,
        dataWarning: isWarning,
        statusList,
        invoiceList,
        customerOptions,
        isModalStatusOpen,
        statusToEdit,
        client,
        isTourOpen,
        userAccount,
        termOptions,
        customerByClient,
        customerByClientPaginated,
        projectInformation,
        templateList,
        usersList,
        userOptions,
        projectList,
        projectListPaginated,
        projectTemplateList,
        activityList,
        taskList,
        taskDashboard,
        contactList,
        contactListPaginated,
        todoList,
        tagList,
        taskModal,
        projectModal,
        activityModal,
        contactModal,
        projectSelected,
        templateSelected,
        projectsByTemplate,
        statusByTemplate,
        userGroups,
        customerFiles,
        downloadedFile,
        files,
        handleChangeTemplateSelected: (value: ProjectTemplateProps | null) => {
          if (!value) {
            setProjectsByTemplate([]);
            setTemplateSelected(null);
            return;
          }
          setTemplateSelected(value);
        },
        handleChangeProjectSelected,
        handleTour,
        openCloseModalStatus: (value: boolean) => SetIsModalStatusOpen(value),
        handleWarning,
        handleSuccess,
        selectStatusToEdit: (value: StatusProps | null) =>
          setStatusToEdit(value),
        resetContacts,
        getData,
        getDataById,
        getReportData,
        handleCreateData,
        handleEditData,
        handleBulkEdit,
        handleDeleteData,
        handleBulkDeleteData,
        fetchUserAccounts,
        getQbCompanyInfo,
        getCustomersListByClient,
        getCustomersListByClientPaginated,
        getCustomersListByProject,
        openItemModal,
        closeItemModal,
        handleChangeProjectModalStatus,
        createOrUpdateTask,
        createOrUpdateActivity,
        createOrUpdateContact,
        uploadFile,
        downloadFile,
        createFile,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export default DataProvider;
